import { Link, useLocation } from 'react-router-dom';
import {
    Home,
    Globe,
    LogOut,
    LinkIcon,
    Menu,
    X,
    BrickWall,
    History,
    KeyIcon,
    Bell,
    ChevronDown,
    ChevronRight,
    Settings,
    Facebook,
    MessageCircle,
    MessagesSquare,
    Eye,
    TrendingUp,
    Activity
} from 'lucide-react';
import { cn } from '@/lib/utils.ts';
import { Button } from '../ui/button';
import { clearAuthData, getCurrentUser } from '@/services/api';
import { podeVerChatbot, podeVerMetaAds } from '@/lib/acessoRestrito';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';

/** Item folha do menu. `badge` marca novidade — hoje só 'new'. */
interface SidebarLeaf {
    title: string;
    href: string;
    badge?: 'new';
}

interface SidebarChild {
    title: string;
    href: string;
    /** true = só admin vê. A API também barra; aqui é para não oferecer o que dá 403. */
    adminOnly?: boolean;
    children?: SidebarLeaf[];
}

interface SidebarItem {
    title: string;
    href: string;
    icon: React.ComponentType<{ className?: string }>;
    adminOnly?: boolean;
    children?: SidebarChild[];
}

const sidebarItems: SidebarItem[] = [
    { title: 'Dashboard', href: '/dashboard', icon: Home },
    { title: 'Domains', href: '/domain', icon: Globe },
    { title: 'Offerwall', href: '/offerwall', icon: BrickWall },
    { title: 'Keyvalues', href: '/custom-targeting', icon: KeyIcon },
    { title: 'Reports', href: '/reports', icon: History },
    { title: 'LTV', href: '/ltv', icon: TrendingUp },
    { title: 'Blocks', href: '/blocks', icon: LinkIcon },
    { title: 'Chatbots', href: '/chatbot', icon: MessagesSquare },
    {
        title: 'Pageview',
        href: '/pageview',
        icon: Eye,
        children: [
            { title: 'Analytics', href: '/pageview' },
            { title: 'Redirect Groups', href: '/pageview/redirect-groups' },
        ],
    },
    {
        title: 'Gam',
        href: '/gam',
        icon: Settings,
        children: [
            { title: 'URLs', href: '/urls' },
            // Regras de inventário são pares custom_key/custom_value válidos
            // para a network inteira do GAM — não pertencem a site nenhum, e a
            // API responde 403 para gestor.
            { title: 'Inventory Rules', href: '/inventory-rules', adminOnly: true },
            { title: 'Price Rules', href: '/price-rules' },
            { title: 'Price Cascade', href: '/price-cascades' },
            { title: 'Price Structure', href: '/price-structure' },
            { title: 'Cascade Blocks', href: '/cascade-blocks' },
        ],
    },
    {
        title: 'Push Notification',
        href: '/push',
        icon: Bell,
        children: [
            { title: 'Dashboard', href: '/push' },
            { title: 'Setup', href: '/push/setup' },
            { title: 'Campanhas', href: '/push/campaigns' },
            { title: 'Leads', href: '/push/leads' },
            { title: 'Flows', href: '/push/flows' },
            { title: 'Grupos', href: '/push/groups' },
        ],
    },
    {
        title: 'Meta Ads',
        href: '/meta',
        icon: Facebook,
        children: [
            { title: 'Conexões', href: '/meta/connection' },
            {
                title: 'Messageria',
                href: '/meta/flow',
                children: [
                    { title: 'Analytics', href: '/meta/analytics' },
                    { title: 'Leads', href: '/meta/leads-dashboard', badge: 'new' },
                    { title: 'Flow', href: '/meta/flow' },
                    { title: 'Broadcasts', href: '/meta/broadcasts' },
                    { title: 'Utility Templates', href: '/meta/utility-templates' },
                    { title: 'Esteiras de Utility', href: '/meta/utility-esteiras' },
                    // LEGADO: a antiga tela de Leads (/meta/leads) saiu do menu
                    // em 25/09. Ela mede outra coisa e diverge da nova em quase
                    // todo número — a nova conta receita medida por
                    // utm_campaign, ignora página pausada no estoque e separa
                    // flow de broadcast. Manter as duas à vista produzia
                    // discussão sobre qual está certa.
                    //
                    // A ROTA CONTINUA DE PÉ em App.tsx: há links antigos
                    // salvos e relatórios que apontam para lá. Só não é mais
                    // oferecida. Para remover de vez, apagar a rota junto.
                    { title: 'Auto Replies', href: '/meta/comment-auto-replies' },
                    { title: 'Debug de página', href: '/meta/page-debug' },
                ],
            },
        ],
    },
    // A resposta expõe a topologia interna da VPS (portas e serviços), então a
    // API responde 403 para gestor e o item não é oferecido a quem não é admin.
    { title: 'Status', href: '/system-health', icon: Activity, adminOnly: true },
    {
        title: 'Configurations',
        href: '/settings',
        icon: Settings,
        children: [
            { title: 'Usuários e Acessos', href: '/settings/domain-access' },
            { title: 'AI Keys', href: '/settings/ai-keys' },
            { title: 'Page Access', href: '/settings/page-access' },
            { title: 'Users', href: '/settings/users' },
        ],
    },
];

export default function Sidebar() {
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [isOpen, setIsOpen] = useState(false);
    const currentUser = getCurrentUser();
    const isAdmin = currentUser?.role === 'admin';
    // Esconde o que a API recusaria de qualquer forma. É conveniência de
    // navegação, não segurança: o papel vem do localStorage e é editável no
    // DevTools — quem barra de verdade é a API.
    const visibleItems = sidebarItems
        .filter((item) => {
            if (item.href === '/settings' && !isAdmin) return false;
            if (item.href === '/meta' && !podeVerMetaAds(currentUser)) return false;
            if (item.href === '/chatbot' && !podeVerChatbot(currentUser)) return false;
            if (item.adminOnly && !isAdmin) return false;
            return true;
        })
        .map((item) => {
            if (!item.children || isAdmin) return item;
            const children = item.children.filter((c) => !c.adminOnly);
            return { ...item, children };
        })
        // Menu que ficou sem nenhum filho (todos eram adminOnly) some junto.
        .filter((item) => !item.children || item.children.length > 0);
    const [expandedMenus, setExpandedMenus] = useState<string[]>(['meta-messageria']);
    // Auto-expand menus if on their route
    useEffect(() => {
        if (location.pathname.startsWith('/push')) {
            setExpandedMenus((prev) => prev.includes('/push') ? prev : [...prev, '/push']);
        }
        if (location.pathname.startsWith('/meta')) {
            setExpandedMenus((prev) => {
                let next = prev.includes('/meta') ? prev : [...prev, '/meta'];
                const messageriaRoutes = ['/meta/analytics', '/meta/leads-dashboard', '/meta/flow', '/meta/broadcasts', '/meta/utility-esteiras', '/meta/leads', '/meta/comment-auto-replies', '/meta/page-debug'];
                if (messageriaRoutes.some(r => location.pathname.startsWith(r))) {
                    next = next.includes('meta-messageria') ? next : [...next, 'meta-messageria'];
                }
                return next;
            });
        }
        if (location.pathname.startsWith('/settings')) {
            setExpandedMenus((prev) => prev.includes('/settings') ? prev : [...prev, '/settings']);
        }
        const gamRoutes = ['/urls', '/inventory-rules', '/price-rules', '/price-cascades', '/price-structure', '/cascade-blocks'];
        if (gamRoutes.some(r => location.pathname.startsWith(r))) {
            setExpandedMenus((prev) => prev.includes('/gam') ? prev : [...prev, '/gam']);
        }
        if (location.pathname.startsWith('/pageview') || location.pathname === '/redirect-groups') {
            setExpandedMenus((prev) => prev.includes('/pageview') ? prev : [...prev, '/pageview']);
        }
    }, [location.pathname]);

    const toggleMenu = (key: string) => {
        setExpandedMenus((prev) =>
            prev.includes(key) ? prev.filter((h) => h !== key) : [...prev, key]
        );
    };

    const handleLogout = () => {
        queryClient.clear();
        clearAuthData();
        navigate('/login');
    };

    useEffect(() => { setIsOpen(false); }, [location.pathname]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const sidebar = document.getElementById('sidebar');
            const menuButton = document.getElementById('menu-button');
            if (isOpen && sidebar && !sidebar.contains(event.target as Node) && menuButton && !menuButton.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : 'unset';
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    return (
        <>
            <button
                id="menu-button"
                onClick={() => setIsOpen(!isOpen)}
                className="fixed top-4 left-4 z-50 lg:hidden bg-gray-900 text-white p-2 rounded-lg shadow-lg hover:bg-gray-800 transition-colors"
                aria-label="Toggle menu"
            >
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>

            {isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden" onClick={() => setIsOpen(false)} />
            )}

            <div
                id="sidebar"
                className={cn(
                    'fixed lg:static inset-y-0 left-0 z-40 flex h-screen w-64 flex-col bg-gray-900 text-white transition-transform duration-300 ease-in-out',
                    isOpen ? 'translate-x-0' : '-translate-x-full',
                    'lg:translate-x-0'
                )}
            >
                <div className="flex h-16 items-center justify-center border-b border-gray-800">
                    <h1 className="text-xl font-bold">Rocket</h1>
                </div>

                <nav className="flex-1 space-y-1 overflow-y-auto p-4">
                    {visibleItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = item.children
                            ? item.children.some((child: SidebarChild) => {
                                if (child.children) {
                                    return child.children.some(sc => location.pathname.startsWith(sc.href));
                                }
                                return location.pathname.startsWith(child.href);
                            })
                            : location.pathname === item.href;
                        const isExpanded = expandedMenus.includes(item.href);

                        if (item.children) {
                            return (
                                <div key={item.href}>
                                    <button
                                        onClick={() => toggleMenu(item.href)}
                                        className={cn(
                                            'flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                                            isActive ? 'bg-gray-800 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                        )}
                                    >
                                        <div className="flex items-center gap-3">
                                            <Icon className="h-5 w-5" />
                                            {item.title}
                                        </div>
                                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                    </button>
                                    {isExpanded && (
                                        <div className="ml-4 mt-1 space-y-1 border-l border-gray-700 pl-4">
                                            {item.children.map((child: SidebarChild) => {
                                                if (child.children) {
                                                    const subKey = `${item.href.replace('/', '')}-${child.title.toLowerCase()}`;
                                                    const isSubExpanded = expandedMenus.includes(subKey);
                                                    const isSubActive = child.children.some((sc: SidebarLeaf) => location.pathname.startsWith(sc.href));

                                                    return (
                                                        <div key={subKey}>
                                                            <button
                                                                onClick={() => toggleMenu(subKey)}
                                                                className={cn(
                                                                    'flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-sm transition-colors',
                                                                    isSubActive ? 'text-white font-medium' : 'text-gray-500 hover:text-white'
                                                                )}
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    <MessageCircle className="h-3.5 w-3.5" />
                                                                    {child.title}
                                                                </div>
                                                                {isSubExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                                            </button>
                                                            {isSubExpanded && (
                                                                <div className="ml-3 mt-0.5 space-y-0.5 border-l border-gray-700/50 pl-3">
                                                                    {child.children.map((subChild: SidebarLeaf) => (
                                                                        <Link
                                                                            key={subChild.href}
                                                                            to={subChild.href}
                                                                            className={cn(
                                                                                'flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm transition-colors',
                                                                                location.pathname.startsWith(subChild.href)
                                                                                    ? 'text-white font-medium'
                                                                                    : 'text-gray-500 hover:text-white'
                                                                            )}
                                                                        >
                                                                            {subChild.title}
                                                                            {subChild.badge === 'new' && (
                                                                                <span className="animate-pulse rounded bg-blue-500 px-1.5 py-[1px] text-[9px] font-semibold uppercase tracking-wide text-white">
                                                                                    New
                                                                                </span>
                                                                            )}
                                                                        </Link>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                return (
                                                    <Link
                                                        key={child.href}
                                                        to={child.href}
                                                        className={cn(
                                                            'block rounded-lg px-3 py-1.5 text-sm transition-colors',
                                                            location.pathname === child.href
                                                                ? 'text-white font-medium'
                                                                : 'text-gray-500 hover:text-white'
                                                        )}
                                                    >
                                                        {child.title}
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        const isItemActive = location.pathname === item.href;
                        return (
                            <Link
                                key={item.href}
                                to={item.href}
                                className={cn(
                                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                                    isItemActive ? 'bg-gray-800 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                )}
                            >
                                <Icon className="h-5 w-5" />
                                {item.title}
                            </Link>
                        );
                    })}
                </nav>

                <div className="border-t border-gray-800 p-4">
                    <Button
                        variant="ghost"
                        className="w-full justify-start text-gray-400 hover:bg-gray-800 hover:text-white"
                        onClick={handleLogout}
                    >
                        <LogOut className="mr-3 h-5 w-5" />
                        Sair
                    </Button>
                </div>
            </div>
        </>
    );
}